import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
     dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
     dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "/retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏠",
    title: "Property Listing & Marketplace",
    desc: "Full-featured real estate portals with advanced search filters, map-based property discovery, verified listings, agent profiles, lead capture, and mortgage calculator — built for buyers, sellers, and brokers.",
    tags: ["Google Maps API", "Advanced Filters", "Lead Capture", "Verified Listings"],
    color: "#B45309",
  },
  {
    icon: "🥽",
    title: "3D Virtual Tours & AR Walkthroughs",
    desc: "Immersive 3D virtual property tours, augmented reality furniture placement, 360° photography integration, and interactive floor plan viewers — letting buyers explore homes from anywhere in the world.",
    tags: ["Three.js", "WebGL", "Matterport", "AR/VR"],
    color: "#0891B2",
  },
  {
    icon: "🤖",
    title: "AI Property Valuation & Recommendations",
    desc: "ML-powered automated valuation models (AVM) using location data, comparable sales, amenities, and market trends — plus AI-driven property recommendations personalised to each buyer's preferences.",
    tags: ["AVM", "ML Models", "Recommendation Engine", "Market Analytics"],
    color: "#7C3AED",
  },
  {
    icon: "📋",
    title: "CRM for Real Estate Agents & Brokers",
    desc: "Purpose-built real estate CRM with lead pipeline management, automated follow-ups, WhatsApp/SMS integration, deal tracking, commission management, and performance dashboards for agents and teams.",
    tags: ["Lead Pipeline", "WhatsApp API", "Deal Tracking", "Commission Mgmt"],
    color: "#059669",
  },
  {
    icon: "🏗️",
    title: "Property Management System",
    desc: "End-to-end property management for landlords and property managers — tenant onboarding, rent collection, maintenance requests, document management, lease renewals, and financial reporting.",
    tags: ["Tenant Management", "Rent Collection", "Maintenance", "Lease Mgmt"],
    color: "#DC2626",
  },
  {
    icon: "📊",
    title: "Real Estate Analytics & BI Dashboard",
    desc: "Market intelligence dashboards with price trend analysis, demand heatmaps, inventory forecasting, comparable market analysis (CMA), and ROI calculators for investors and developers.",
    tags: ["Price Trends", "Heatmaps", "CMA", "ROI Calculator"],
    color: "#D97706",
  },
  {
    icon: "📝",
    title: "Digital Transaction & Document Management",
    desc: "End-to-end digital transaction management — e-signatures, document templates, compliance checklists, title deed management, stamp duty calculators, and RERA-compliant documentation workflows.",
    tags: ["e-Signature", "RERA Compliance", "DocuSign", "Stamp Duty"],
    color: "#4A7C59",
  },
  {
    icon: "💳",
    title: "PropTech Payments & EMI Management",
    desc: "Secure payment collection for bookings, token amounts, and installments — with automated payment schedules, EMI tracking, demand letter generation, and reconciliation dashboards for developers.",
    tags: ["Razorpay", "EMI Tracking", "Demand Letters", "Reconciliation"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    items: [
      { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & AI",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Django", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/django/django-plain.svg", color: "#092E20" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg", color: "#FF6F00" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", color: "#FF2D20" },
    ],
  },
  {
    category: "Maps & 3D",
    items: [
      { name: "Google Maps API", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/google/google-original.svg", color: "#4285F4" },
      { name: "Mapbox", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#000000" },
      { name: "Three.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/threejs/threejs-original.svg", color: "#000000" },
      { name: "WebGL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/opengl/opengl-original.svg", color: "#5586A4" },
      { name: "Cesium.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#6CADDF" },
      { name: "Matterport SDK", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#D62B2B" },
    ],
  },
  {
    category: "Cloud & Database",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Market & Requirements Discovery", desc: "Deep-dive workshops with developers, brokers, agents, and buyers to map user journeys, property data models, RERA compliance requirements, integration needs, and competitive positioning before any wireframe is drawn.", color: "#B45309" },
  { n: "02", icon: "🎨", title: "UX Research & Property-Centric Design", desc: "User research with real estate buyers, sellers, and agents. Trust-first, conversion-optimised UI — property cards, search UX, and inquiry flows designed to maximise lead quality and reduce time-to-contact.", color: "#0891B2" },
  { n: "03", icon: "⚙️", title: "Agile Development with PropTech QA", desc: "2-week sprints with dedicated real estate QA — property search accuracy testing, map rendering benchmarking, 3D tour performance validation, and lead flow testing, with weekly staging demos you can explore.", color: "#7C3AED" },
  { n: "04", icon: "🗺️", title: "Maps, 3D & Data Integrations", desc: "Google Maps / Mapbox integration with custom property layers, 3D virtual tour embedding, MLS/property data feed ingestion, government land record APIs, RERA portal integration, and payment gateway connections.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "SEO Audit, UAT & Performance Testing", desc: "Technical SEO audit for property listing pages, structured data implementation, Core Web Vitals optimisation, cross-device QA across 20+ combinations, load testing for peak traffic, and structured UAT with real agents.", color: "#DC2626" },
  { n: "06", icon: "🚀", title: "Launch, SEO Growth & Continuous Iteration", desc: "Phased launch with content migration, agent onboarding, 24/7 SLA support, monthly SEO performance reviews, A/B testing of listing page elements, and continuous feature iteration driven by lead analytics and agent feedback.", color: "#4A7C59" },
];

const CASE_STUDIES = [
  {
    title: "GetMyProperty — Pan-India Portal",
    industry: "Property Marketplace",
    desc: "A pan-India real estate marketplace with 3D virtual tours, AI property valuation, instant mortgage calculators, and verified agent profiles — covering 15 cities, 50,000+ listings, and generating 3,500+ qualified leads per month.",
    img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Google Maps API", "Three.js", "Node.js"],
    metrics: [{ val: "50K+", lbl: "Active Listings" }, { val: "3,500+", lbl: "Leads/Month" }, { val: "15", lbl: "Cities Covered" }],
    color: "#B45309",
  },
  {
    title: "NestIQ — AI-Powered Property CRM",
    industry: "Real Estate CRM",
    desc: "A purpose-built real estate CRM for a 500-agent brokerage network — AI lead scoring, automated WhatsApp follow-ups, deal pipeline tracking, commission management, and a performance leaderboard. Agent productivity increased by 68% in 3 months.",
    img: "https://images.unsplash.com/photo-1582407947304-fd86f28f2e26?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Python", "WhatsApp API", "PostgreSQL"],
    metrics: [{ val: "68%", lbl: "Agent Productivity ↑" }, { val: "500+", lbl: "Agents Onboarded" }, { val: "42%", lbl: "Faster Deal Closure" }],
    color: "#059669",
  },
  {
    title: "LuxeVillas — Premium Property Platform",
    industry: "Luxury Real Estate",
    desc: "A luxury real estate platform for a Tier-1 developer — immersive 3D walkthrough tours, AR furniture placement, unit availability matrix, RERA-compliant documentation workflow, and an EMI booking engine that processed ₹480Cr in bookings at launch.",
    img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Three.js", "Razorpay", "DocuSign"],
    metrics: [{ val: "₹480Cr", lbl: "Bookings at Launch" }, { val: "3D", lbl: "Virtual Tours" }, { val: "RERA", lbl: "Fully Compliant" }],
    color: "#7C3AED",
  },
];

const WHY_US = [
  { icon: "🏠", title: "Deep Real Estate Domain Knowledge", desc: "Our team includes ex-PropTech product managers, real estate agents, and developers who understand buyer psychology, agent workflows, RERA compliance, and property data models — not just web development.", color: "#B45309" },
  { icon: "📍", title: "Advanced Maps & Location Intelligence", desc: "Google Maps, Mapbox, and custom GIS layers with property boundary overlays, transit proximity scoring, school catchment mapping, and neighbourhood heatmaps — location intelligence that converts browsers into buyers.", color: "#0891B2" },
  { icon: "🥽", title: "3D & Immersive Property Experiences", desc: "Three.js 3D floor plans, Matterport virtual tour integration, WebGL property visualisers, and AR furniture placement — immersive experiences that reduce site visit requirements and accelerate purchase decisions.", color: "#7C3AED" },
  { icon: "🤖", title: "AI That Drives Qualified Leads", desc: "ML-powered property recommendation engines, automated valuation models, buyer intent scoring, and AI chatbots that qualify leads 24/7 — delivering higher-quality enquiries to your agents.", color: "#DC2626" },
  { icon: "📋", title: "RERA & Compliance-Ready Architecture", desc: "RERA portal integration, RERA-compliant project pages, stamp duty calculators, allotment letter generation, payment schedule management, and audit-ready transaction records built into every developer platform.", color: "#D97706" },
  { icon: "📈", title: "SEO-First Property Portals", desc: "Technical SEO architecture for property listings — structured data (Schema.org/RealEstateListing), dynamic sitemap generation, canonical URL management, Core Web Vitals optimisation, and hyperlocal content strategies.", color: "#4A7C59" },
];

const FAQS = [
  {
    q: "Can you build a property portal that competes with MagicBricks or 99acres?",
    a: "Yes — we've done it. We build full-featured property marketplaces with advanced search (location, price, type, BHK, amenities, possession date), map-based discovery with custom property layers, verified listing workflows, agent and builder profiles, lead management dashboards, and SEO-optimised property pages. The key differentiator is usually niche focus (luxury, commercial, specific city, NRI buyers) combined with a superior UX and faster load times than incumbent portals. We also implement Schema.org structured data and technical SEO from day one.",
  },
  {
    q: "How do you implement 3D virtual tours — do buyers need to download anything?",
    a: "No download required. We implement browser-based 3D experiences using Three.js and WebGL — accessible on any device, including mobile, with no app install. For developer projects, we build interactive 3D floor plan viewers showing unit availability, floor levels, and view angles. For completed properties, we integrate Matterport 360° tours or build custom WebGL walkthroughs. AR furniture placement works on iOS (ARKit) and Android (ARCore) through a lightweight web-based experience. All experiences are lazy-loaded and optimised for Indian mobile connection speeds.",
  },
  {
    q: "How do you handle property data and listing management at scale?",
    a: "We architect property data as a structured schema with full-text search (Elasticsearch) and geospatial indexing (PostGIS in PostgreSQL). Listing ingestion supports manual entry, CSV/Excel bulk upload, XML data feed imports from builders, and API connections to existing inventory management systems. Image processing is automated — auto-compression, WebP conversion, and CDN delivery. For large portals with 100K+ listings, we implement database sharding, Redis caching for search results, and Elasticsearch for near-instant search responses even on complex filter combinations.",
  },
  {
    q: "Can you integrate with RERA portals and government land record systems?",
    a: "Yes. We integrate with state RERA portals for project registration status verification and compliance data display. For government land records, we've integrated with portals in Maharashtra (IGR), Delhi (DLR), Karnataka, and several other states. We also integrate with stamp duty calculators (jurisdiction-specific rates), IGRS for encumbrance certificate lookups, and DigiLocker for property document storage. Each state has different APIs and data formats — we handle the mapping and keep integrations updated as portals change.",
  },
  {
    q: "How do you make a property portal rank well on Google?",
    a: "Technical SEO is built into our architecture, not added later. Every property listing page gets programmatically generated titles, meta descriptions, and Schema.org RealEstateListing structured data. We implement dynamic XML sitemaps that update automatically as listings are added or removed. City, locality, and property-type landing pages are designed for hyperlocal keyword targeting. We implement canonical URLs to prevent duplicate content from filter permutations, lazy loading for images, and Core Web Vitals optimisation. We've consistently achieved first-page rankings for competitive real estate keywords within 4–6 months of launch.",
  },
  {
    q: "What post-launch support do you offer for real estate platforms?",
    a: "All real estate projects come with a 3-month warranty covering bug fixes. Beyond that, our support retainers include 24/7 uptime monitoring, monthly SEO performance reports, lead analytics reviews, listing data quality audits, security patches, and quarterly feature releases driven by agent and buyer feedback. Real estate platforms need continuous evolution — new property types, new RERA requirements, new payment integrations, and new search features are the norm. Most clients retain us as their long-term technology partner.",
  },
];

const STATS = [
  { val: "140+", lbl: "PropTech Projects Delivered", icon: "🏠" },
  { val: "500K+", lbl: "Property Listings Managed", icon: "🏗️" },
  { val: "₹2,400Cr+", lbl: "Property Transactions Enabled", icon: "💰" },
  { val: "RERA", lbl: "Compliance Ready", icon: "✅" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #D6C4A0" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 28px rgba(28,43,58,.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#1C2B3A,#2D4A3E,#4A7C59)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => (
            <div key={link.label} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {link.link ? (
                <a href={link.link} style={{
                  background: currentPath === link.link ? "#F5F0E8" : "none",
                  border: currentPath === link.link ? "1px solid #D6C4A0" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: currentPath === link.link ? "#B45309" : "#374151",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none",
                  transition: "all .2s",
                }}>{link.label}</a>
              ) : (
                <button style={{
                  background: link.label === "Industries" ? "#F5F0E8" : "none",
                  border: link.label === "Industries" ? "1px solid #D6C4A0" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: link.label === "Industries" ? "#B45309" : activeDropdown === link.label ? "#B45309" : "#374151",
                  display: "flex", alignItems: "center", gap: 4, transition: "all .2s",
                  fontFamily: "'Sora',sans-serif", borderRadius: 8,
                }}>
                  {link.label}
                  {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>}
                </button>
              )}
              {link.dropdown && activeDropdown === link.label && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 200,
                  background: "#fff", border: "1px solid #D6C4A0", borderRadius: 16,
                  boxShadow: "0 20px 60px rgba(28,43,58,.1)", minWidth: 230,
                  padding: "8px 0", animation: "fadeIn .2s ease",
                }}>
                  {link.dropdown.map(item => (
                    <a key={item.name} href={item.link} style={{
                      display: "block", padding: "10px 20px", fontSize: 13.5,
                      color: item.name === "Real Estate" ? "#B45309" : "#374151",
                      background: item.name === "Real Estate" ? "#F5F0E8" : "none",
                      fontWeight: item.name === "Real Estate" ? 700 : 500,
                      textDecoration: "none", transition: "all .15s",
                      fontFamily: "'Sora',sans-serif",
                    }}
                      onMouseEnter={e => { e.target.style.background = "#F5F0E8"; e.target.style.color = "#B45309"; }}
                      onMouseLeave={e => {
                        e.target.style.background = item.name === "Real Estate" ? "#F5F0E8" : "none";
                        e.target.style.color = item.name === "Real Estate" ? "#B45309" : "#374151";
                      }}
                    >{item.name}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1C2B3A,#2D4A3E)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(28,43,58,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(28,43,58,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(28,43,58,.3)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#F5F0E8" : "none",
            border: "1px solid #D6C4A0", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>{mobileOpen ? "✕" : "☰"}</button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #D6C4A0",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,.1)",
        }}>
          <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #F5F0E8" }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)} style={{
                      width: "100%", textAlign: "left", padding: "13px 24px",
                      background: mobileDropdown === link.label ? "#F5F0E8" : "none",
                      border: "none", borderBottom: "1px solid #F5F0E8",
                      color: mobileDropdown === link.label ? "#B45309" : "#374151",
                      fontWeight: 600, fontSize: 15, fontFamily: "'Sora',sans-serif",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      {link.label}
                      <span style={{ fontSize: 11, color: "#9CA3AF", transition: "transform .2s", transform: mobileDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#FAF7F2", borderBottom: "1px solid #F5F0E8" }}>
                        {link.dropdown.map(item => (
                          <a key={item.name} href={item.link} onClick={() => setMobileOpen(false)} style={{
                            display: "block", padding: "11px 24px 11px 40px", fontSize: 14,
                            color: item.name === "Real Estate" ? "#B45309" : "#4B5563",
                            background: item.name === "Real Estate" ? "#F5F0E8" : "none",
                            fontWeight: item.name === "Real Estate" ? 700 : 500,
                            textDecoration: "none", borderBottom: "1px solid #F5F0E8",
                            fontFamily: "'Sora',sans-serif",
                          }}>→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={link.link || "#"} onClick={() => setMobileOpen(false)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "13px 24px",
                    color: currentPath === link.link ? "#B45309" : "#374151",
                    background: currentPath === link.link ? "#F5F0E8" : "none",
                    fontWeight: 600, fontSize: 15, textDecoration: "none",
                    borderBottom: "1px solid #F5F0E8", fontFamily: "'Sora',sans-serif",
                  }}>
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a href="#contact" onClick={() => setMobileOpen(false)} style={{
              background: "linear-gradient(135deg,#1C2B3A,#2D4A3E)", color: "#fff",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", boxShadow: "0 6px 20px rgba(28,43,58,.3)",
            }}>🏠 Get Free Consultation</a>
            <a href="tel:+919646206032" style={{
              background: "#F5F0E8", color: "#1C2B3A",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", border: "1px solid #D6C4A0",
            }}>📞 Call: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["RERA Compliance Ready", "500K+ Listings Managed", "₹2,400Cr+ Transactions Enabled", "140+ PropTech Projects Delivered"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(155deg,#F5F0E8 0%,#EFF4EF 35%,#EEF2F7 65%,#F8FAFC 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(28,43,58,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(28,43,58,.04) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Curved blobs */}
      <div style={{ position: "absolute", top: -180, right: -120, width: 700, height: 700, borderRadius: "61% 39% 70% 30%/30% 50% 50% 70%", background: "radial-gradient(circle,rgba(180,83,9,.08),rgba(74,124,89,.05) 50%,transparent 75%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -80, width: 560, height: 560, borderRadius: "39% 61% 30% 70%/70% 30% 70% 30%", background: "radial-gradient(circle,rgba(45,74,62,.07),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,58,237,.05),transparent 70%)", pointerEvents: "none" }} />

      {/* Decorative property icons */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { x: "6%", y: "18%", sym: "🏠" },
          { x: "91%", y: "14%", sym: "🏗️" },
          { x: "14%", y: "78%", sym: "🗺️" },
          { x: "88%", y: "72%", sym: "🔑" },
          { x: "52%", y: "90%", sym: "📍" },
        ].map((item, i) => (
          <div key={i} style={{
            position: "absolute", left: item.x, top: item.y,
            fontSize: 20,
            opacity: 0.08 + i * 0.02,
            animation: `rePulse ${3 + i}s ease-in-out infinite ${i * 0.6}s`,
          }}>{item.sym}</div>
        ))}
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#9CA3AF" }}>
          <a href="/" style={{ color: "#B45309", textDecoration: "none", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Home</a>
          <span>›</span>
          <span style={{ fontFamily: "'Sora',sans-serif" }}>Industries</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Real Estate</span>
        </div>

        <div className="re-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F5F0E8", border: "1px solid #D6C4A0", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#B45309", display: "inline-block", animation: "rePulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1C2B3A", fontFamily: "'Sora',sans-serif" }}>🏠 India's Trusted PropTech Development Partner</span>
            </div>

            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(36px,4.8vw,64px)",
              fontWeight: 900, color: "#1C2B3A",
              lineHeight: 1.1, marginBottom: 20,
            }}>
              Real Estate Software<br />
              <span style={{ background: "linear-gradient(135deg,#B45309,#4A7C59,#0891B2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Built to Close Deals,
              </span><br />
              Engineered to Scale.
            </h1>

            <p style={{ fontSize: 17, color: "#4B5563", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'Sora',sans-serif", fontWeight: 400 }}>
              We build RERA-compliant property marketplaces, AI-powered real estate CRMs, 3D virtual tour platforms, property management systems, and PropTech SaaS products — engineered for the trust, scale, and conversion demands of modern property transactions.
            </p>

            {/* Rotating metric pill */}
            <div style={{ background: "#F5F0E8", border: "1px solid #D6C4A0", borderRadius: 12, padding: "13px 20px", marginBottom: 36, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#1C2B3A" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#1C2B3A,#2D4A3E)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(28,43,58,.35)",
                transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(28,43,58,.48)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(28,43,58,.35)"; }}
              >🏠 Start PropTech Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#1C2B3A",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "2px solid #D6C4A0",
                transition: "all .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F5F0E8"; e.currentTarget.style.borderColor = "#B45309"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#D6C4A0"; }}
              >📂 View Case Studies</a>
            </div>

            {/* Trust signal row */}
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
              {[["140+", "PropTech Projects"], ["500K+", "Listings Managed"], ["₹2,400Cr+", "Transactions"], ["RERA", "Compliant"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#B45309" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", fontWeight: 600, marginTop: 2, fontFamily: "'Sora',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual panel */}
          <div style={{ position: "relative" }}>
            {/* Main image card */}
            <div style={{
              borderRadius: 28, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(28,43,58,.14), 0 0 0 1px rgba(28,43,58,.08)",
              background: "#fff", position: "relative",
            }}>
              {/* Dashboard chrome bar */}
              <div style={{ background: "#FAF7F2", borderBottom: "1px solid #D6C4A0", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#F5F0E8", borderRadius: 8, padding: "5px 14px", fontSize: 12, color: "#B45309", fontWeight: 600, margin: "0 12px", fontFamily: "'Sora',sans-serif" }}>
                  🔒 getmyproperty.techvanto.com
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, fontFamily: "'Sora',sans-serif", background: "#F5F0E8", padding: "4px 10px", borderRadius: 99, border: "1px solid #D6C4A0" }}>LIVE</div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=640&h=380&fit=crop&auto=format"
                alt="Real Estate Platform"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
              {/* Overlay gradient */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100, background: "linear-gradient(to top,rgba(28,43,58,.3),transparent)" }} />
            </div>

            {/* Floating badge — RERA */}
            <div style={{ position: "absolute", top: -18, left: -22, background: "#fff", borderRadius: 18, padding: "14px 18px", boxShadow: "0 12px 40px rgba(28,43,58,.18)", border: "1.5px solid #D6C4A0", animation: "float 3.2s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#1C2B3A,#4A7C59)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✅</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#1C2B3A" }}>RERA</div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>Fully Compliant</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Transactions */}
            <div style={{ position: "absolute", bottom: 20, right: -22, background: "linear-gradient(135deg,#1C2B3A,#2D4A3E)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(28,43,58,.4)", animation: "float 3.8s ease-in-out infinite .6s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff" }}>₹2,400Cr+</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Transactions 💰</div>
            </div>

            {/* Floating badge — 3D Tours */}
            <div style={{ position: "absolute", bottom: -18, left: 16, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.09)", border: "1.5px solid #D1FAE5", animation: "float 4.2s ease-in-out infinite 1.2s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 20 }}>🥽</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>3D Virtual Tours</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>No app required</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Listings */}
            <div style={{ position: "absolute", top: 56, right: -20, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 6px 22px rgba(180,83,9,.12)", border: "1.5px solid #FED7AA", animation: "float 4.6s ease-in-out infinite 1.8s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>🏗️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#92400E", fontFamily: "'Sora',sans-serif" }}>500K+ Listings</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Managed & Live</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#1C2B3A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }} className="re-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(28px,3vw,44px)", fontWeight: 900, color: "#fff",
              lineHeight: 1,
            }}>{s.val}</div>
            <div style={{ color: "#C4A882", fontSize: 13, marginTop: 8, fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="re-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#B45309", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>Real Estate Software Solutions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 580, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>
            Trust-first property technology built for the complexity of real estate — from a 10-agent brokerage to a pan-India developer platform with 100,000+ units.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#F5F0E8"}`,
                boxShadow: hov === i ? `0 28px 72px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden", cursor: "pointer",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}20` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#1C2B3A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'Sora',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Sora',sans-serif" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FAF7F2" }} className="re-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#B45309", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>PropTech Depth Meets Engineering Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 540, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>What separates a generic IT company from a true real estate technology partner.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {WHY_US.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#D6C4A0"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}16` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: `${p.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1.5px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1C2B3A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, fontFamily: "'Sora',sans-serif" }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="re-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4A7C59", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>PropTech Technology Stack</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>The frameworks, tools, and platforms powering our real estate software — chosen for performance, SEO, and 3D rendering capability.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#1C2B3A,#2D4A3E)" : "#fff",
              borderColor: active === i ? "#2D4A3E" : "#D6C4A0",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(28,43,58,.28)" : "none",
            }}>{t.category}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#F5F0E8"}`,
                background: hov === i ? `${tech.color}07` : "#FAF7F2",
                boxShadow: hov === i ? `0 14px 42px ${tech.color}22` : "0 1px 6px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#D6C4A0"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}20` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'Sora',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(155deg,#F5F0E8 0%,#EFF4EF 50%,#EEF2F7 100%)" }} className="re-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4A7C59", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>How We Build Real Estate Software</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>A conversion-focused development process — designed around buyer trust, agent productivity, RERA compliance, and property data at scale.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 24, padding: "34px 30px",
              border: "1.5px solid #D6C4A0",
              boxShadow: "0 4px 20px rgba(28,43,58,.06)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = step.color; e.currentTarget.style.boxShadow = `0 20px 56px ${step.color}16`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#D6C4A0"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(28,43,58,.06)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}10`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}22` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}12`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Sora',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1C2B3A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="re-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>PropTech Platforms We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>Production real estate platforms handling real transactions — with the lead volumes and deal closure metrics that matter in property.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="re-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}1E` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cs.color}44,transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 16px", borderRadius: 99, fontFamily: "'Sora',sans-serif" }}>{cs.industry}</div>
              </div>

              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,40px)", fontWeight: 900, color: "#1C2B3A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'Sora',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0E`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 36, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 28, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'Sora',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}30`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}30`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 68 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#B45309", border: "2px solid #D6C4A0",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F5F0E8"; e.currentTarget.style.borderColor = "#B45309"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#D6C4A0"; }}
          >View All PropTech Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FAF7F2" }} className="re-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1C2B3A", marginTop: 10, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'Sora',sans-serif" }}>Everything you need to know before starting your real estate technology project.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#B45309" : "#D6C4A0"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(180,83,9,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 600, fontSize: 15.5, color: "#1C2B3A", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#1C2B3A,#B45309)" : "#F5F0E8",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#B45309", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #F5F0E8", paddingTop: 18, fontFamily: "'Sora',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function RealEstateSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes rePulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.45; transform:scale(0.96); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .re-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .re-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; text-align:center; }
        .re-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .re-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .re-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .re-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .re-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .re-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .re-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .re-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .re-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .re-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Navbar />
      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}